
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons'
import { IMaskDirective } from 'vue-imask'

import { alphaMask, alphaNumMask } from '~/lib/input-masks/cleanInput'

export default {
  name: 'Input',

  directives: {
    imask: IMaskDirective
  },

  props: {
    value: {
      type: [String, Number],
      default: undefined
    },

    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
    type: {
      type: String,
      default: 'text'
    },

    placeholder: {
      type: String,
      default: null
    },

    hasErrorIcon: {
      type: Boolean,
      default: true
    },

    isLoading: {
      type: Boolean,
      default: false
    },

    mask: {
      type: [Object, String],
      default: () => ({ mask: /.*/ }),
      validator: prop => {
        if (typeof prop === 'object') {
          return !!prop.mask
        } else {
          return ['alpha', 'alphaNum'].includes(prop)
        }
      }
    },

    isNumeric: {
      type: Boolean,
      default: false
    },

    isDisabled: {
      type: Boolean,
      default: false
    },

    autocomplete: {
      type: String,
      default: null
    },

    autocapitalize: {
      type: String,
      default: null
    },

    spellcheck: {
      type: Boolean,
      default: true
    },

    inputmode: {
      type: String,
      default: 'text'
    },

    isReadOnly: {
      type: Boolean,
      default: false
    },

    inputClass: {
      type: String,
      default: ''
    },

    isTransparent: {
      type: Boolean,
      default: false
    },

    // Overrides prefixIcon
    prefixText: {
      type: String,
      default: null
    },

    prefixGrouped: {
      type: Boolean,
      default: false
    },

    prefixNoPadding: {
      type: Boolean,
      default: false
    },

    prefixIcon: {
      type: Object,
      default: null
    },

    // If the start of our input is a dropdown then we need to remove padding
    hasPrefixSelect: {
      type: Boolean,
      default: false
    },

    suffixText: {
      type: String,
      default: null
    },

    suffixGrouped: {
      type: Boolean,
      default: false
    },

    suffixNoPadding: {
      type: Boolean,
      default: false
    },

    suffixIcon: {
      type: Object,
      default: null
    },

    selectAllOnFocus: {
      type: Boolean,
      default: false
    },

    maxLength: {
      type: Number,
      default: null
    },

    dataIndex: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      faExclamationCircle,
      isFocused: false
    }
  },

  computed: {
    compIsGrouped() {
      return this.prefixGrouped || this.suffixGrouped
    },

    compHasPrefix() {
      return this.prefixIcon || this.prefixText || this.$slots.prefix
    },

    compHasSuffix() {
      return this.isLoading || this.suffixIcon || this.suffixText || this.$slots.suffix
    },

    compAutoComplete() {
      if (this.autocomplete !== null) {
        return this.autocomplete
      }

      // The autocomplete attribute is valid on:
      // hidden, text, search, url, tel, email, date, month, week, time, datetime-local, number, range, color, and password.
      return this.type
    },

    compType() {
      // Allow type="number" for convenience but we must transform it
      // "Why the GOV.UK Design System team changed the input type for numbers" https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
      return this.type === 'number' ? 'text' : this.type
    },

    compInputmode() {
      // Allow type="number" for convenience but we must transform it
      // "Why the GOV.UK Design System team changed the input type for numbers" https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
      return this.type === 'number' || this.isNumeric ? 'decimal' : this.inputmode
    }
  },

  watch: {
    mask(newVal) {
      this.setMask(newVal)
    }
  },

  created() {
    this.setMask(this.mask)
  },

  methods: {
    onMaskAccept(event) {
      let newValue = event.detail.value

      // If a number is used then we need the unmasked value, e.g 4444 instead of 4,444
      if (this.isNumeric || this.type === 'number') {
        // When typing a negative number, the unmasked value is '-0' which gets transformed immediately to positive zero
        if (event.detail.unmaskedValue !== '-0') {
          newValue = Number(event.detail.unmaskedValue)
        }
      }

      this.$emit('input', newValue)
    },

    // When mask rules have been completed, we might show a tick or something
    onMaskComplete(event) {
      this.$emit('complete', event)
    },

    handleBlur(event) {
      this.isFocused = false

      this.$emit('blur', event)
    },

    handleFocus(event) {
      this.isFocused = true

      if (this.selectAllOnFocus) {
        event.target.select()
      }

      this.$emit('focus', event)
    },

    setMask(mask) {
      switch (mask) {
        case 'alpha':
          this.privateMask = alphaMask
          break

        case 'alphaNum':
          this.privateMask = alphaNumMask
          break

        default:
          this.privateMask = this.mask
      }
    }
  }
}
